import useIsReadOnlyMode from 'components/v2/compiler/hooks/useIsReadOnlyMode';
import { ImagesUpload } from 'components/v2/images-upload';
import React from 'react';
import useProperty from '../hooks/useProperty';
import usePropertyPath from '../hooks/usePropertyPath';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { MAX_IMG_SIZE } from 'constants/constants';
import { toaster } from 'components/v2/toast';
import { getEpdWriteBlockedMessage } from 'util/epdActionErrorUtils';
import { UploadFileResult } from 'types/dynamic-forms/files/UploadFileResult';
import { t } from 'i18next';
import { useDocumentStore } from '../hooks/useDocumentStore';
import { postUploadFile } from 'services/api/dynamicFormsFilesApi';
import { getFirstOrDefault } from '../utils/getFirstOrDefault';
import { HelpBox } from 'components/v2/help-boxes';

// `single` limits the editor to one image and turns off reordering. The legacy `imageEditor`
// schema type is registered with it, so both image editors share one upload and guard path.
// `showCoverImage` tags the first image as the EPD cover and adds the cover help boxes; only a
// literal `true` in the schema record turns it on.

// The cover tag text, also quoted by the cover help box. Untranslated by design (spec design point 2).
const COVER_LABEL = 'EPD cover';

const ImagesListEditorRenderer: React.FC<any> = ({ propertyId, label, tooltip, single = false, showCoverImage = false }) => {
  const propertyPath = usePropertyPath(propertyId);
  const { value, setValue, errorMessage, isDisabled } = useProperty<Record<string, any>[]>(propertyPath);
  const store = useDocumentStore();
  const { docId } = store;
  const nestedErrorMessage = getFirstOrDefault(store.getErrorMessagesIncludingNested(propertyPath));
  const displayError = errorMessage ?? nestedErrorMessage;
  const isReadOnly = useIsReadOnlyMode();
  const isControlDisabled = isReadOnly || isDisabled;
  const isCoverMode = showCoverImage === true;
  const showReorderHelp = isCoverMode && !isControlDisabled && (value?.length ?? 0) >= 2;

  const onUploadImage = async (docId: string, propertyName: string, file: File) => {
    if (file.size > MAX_IMG_SIZE) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: t('epdWizard.messages.errorTooLargeImageFile', {
          size: Math.floor(MAX_IMG_SIZE / (1024 * 1024)),
        }) as string,
      });

      return;
    }

    let result: UploadFileResult;
    try {
      result = await postUploadFile(docId, {
        propertyName: propertyName,
        propertyId,
        docId: docId,
        file: file,
        documentTypeId: null,
        documentLanguageCode: null,
      });
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: getEpdWriteBlockedMessage(error) ?? 'Could not upload the file. Please try again.',
      });
      return;
    }
    if (result.isSuccessful) setValue([...value, { id: result.model.fileId, name: result.model.fileName || file.name }]);

    return result;
  };

  const handleUploadFile = async (propertyName: string, file: File) => {
    if (isControlDisabled) {
      return null;
    }

    if (!file) {
      return null;
    }

    onUploadImage(docId, propertyName, file);
  };

  const handleReorder = (imgIds: string[]) => {
    if (isControlDisabled) {
      return;
    }

    const map = new Map(value.map((item) => [item.id, item]));
    const reordered = imgIds.map((id) => map.get(id)).filter(Boolean);
    const used = new Set(imgIds);
    const remaining = value.filter((item) => !used.has(item.id));

    const newValue = [...reordered, ...remaining] as Record<string, any>[];

    setValue(newValue);
  };

  const handleRemove = (id: string) => {
    if (isControlDisabled) {
      return;
    }

    setValue(value.filter((img) => img.id !== id));
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {label && <SmartLabel label={label} tooltip={tooltip} hasValue={value && value.length > 0} error={displayError} />}
      <ImagesUpload
        name={propertyId}
        onReorder={single ? () => {} : handleReorder}
        onRemove={handleRemove}
        onUpload={handleUploadFile}
        images={value}
        disabled={isControlDisabled}
        single={single}
        coverLabel={isCoverMode ? COVER_LABEL : undefined}
      />
      {isCoverMode && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
          {showReorderHelp && <HelpBox>Use drag and drop to reorder the images.</HelpBox>}
          <HelpBox>
            {`The image labelled "${COVER_LABEL}" will appear on the product card and on the EPD page in the EPD Library.`}
          </HelpBox>
        </div>
      )}
    </div>
  );
};

export default ImagesListEditorRenderer;
